/**
 * 中间件共用工具函数
 * 提供统一的登录检查、headers 设置、cookie 管理等功能
 */

import { NextRequest, NextResponse } from "next/server"

import { checkLoginStatus } from "./checkLoginStatus"
import { isWwwMetalHost, promoteTemporaryRedirectToPermanent, redirectDefaultLocalePrefix } from "./localeRedirect"
import type { RedirectDefaultLocalePrefixOptions } from "./localeRedirectPath"

const WWW_HOST_REGEXP = /^(test)?www\.metal\.com(?::\d+)?$/i

/**
 * 检查登录状态并返回错误信息
 * @param request - NextRequest 对象
 * @param authTokenKey - 认证 token 的 cookie key
 * @param apiHost - API 主机地址
 * @param defaultLocale - 默认语言
 * @returns 登录错误消息（如果登录失效）或空字符串
 */
export async function checkAuthStatus(
  request: NextRequest,
  authTokenKey: string,
  apiHost: string,
  defaultLocale: string,
  supportedLocales: readonly string[] = []
): Promise<string> {
  const authToken = request.cookies.get(authTokenKey)?.value

  if (!authToken || !apiHost) {
    return ""
  }

  // 从 URL 路径段解析 locale，与 next-intl middleware 保持一致；
  // 无法识别时回退到 defaultLocale。不再读 cookie，避免与 URL 不一致。
  const firstSegment = request.nextUrl.pathname.split("/")[1] ?? ""
  const language = supportedLocales.includes(firstSegment) ? firstSegment : defaultLocale

  const loginError = await checkLoginStatus(authToken, apiHost, language)

  return loginError ? loginError.msg : ""
}

/**
 * 准备传递给服务端组件的 Request Headers
 * @param request - 原始 NextRequest 对象
 * @param authErrorMsg - 登录错误消息（如果有）
 * @returns 包含自定义 headers 的 Headers 对象
 */
export function prepareRequestHeaders(request: NextRequest, authErrorMsg: string): Headers {
  const requestHeaders = new Headers(request.headers)
  requestHeaders.set("x-pathname", request.nextUrl.pathname)

  if (authErrorMsg) {
    requestHeaders.set("X-Auth-Error", encodeURIComponent(authErrorMsg))
  }

  return requestHeaders
}

export function isPrefetchRequest(request: NextRequest): boolean {
  const nextRouterPrefetch = request.headers.get("next-router-prefetch")
  const purpose = request.headers.get("purpose") || request.headers.get("x-purpose")
  const middlewarePrefetch = request.headers.get("x-middleware-prefetch")
  const nextjsData = request.headers.get("x-nextjs-data")

  return Boolean(nextRouterPrefetch) || purpose === "prefetch" || middlewarePrefetch === "1" || Boolean(nextjsData)
}

export function isRscRequest(request: NextRequest): boolean {
  return request.headers.get("Rsc") === "1"
}

export function shouldLogMiddlewareRequest(request: NextRequest): boolean {
  return !isPrefetchRequest(request) && !isRscRequest(request)
}

const SENSITIVE_QUERY_KEYS = new Set(["auth_token", "token"])

function redactSensitiveQuery(urlString: string): string {
  try {
    const parsed = new URL(urlString)
    for (const key of SENSITIVE_QUERY_KEYS) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, "[redacted]")
      }
    }
    return parsed.toString()
  } catch {
    return urlString
  }
}

function redactSearchParams(searchParams: URLSearchParams): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, value] of searchParams.entries()) {
    result[key] = SENSITIVE_QUERY_KEYS.has(key) ? "[redacted]" : value
  }
  return result
}

export function logMiddlewareRequest(
  request: NextRequest,
  options: { defaultLocale: string; locales: readonly string[] }
): void {
  if (!shouldLogMiddlewareRequest(request)) {
    return
  }

  const pathname = request.nextUrl.pathname
  const localeCookie = request.cookies.get("locale")?.value
  const pathSegments = pathname.split("/").filter(Boolean)
  const pathLocale = pathSegments[0] && options.locales.includes(pathSegments[0]) ? pathSegments[0] : null
  const detectedLocale = localeCookie || pathLocale || options.defaultLocale

  // eslint-disable-next-line no-console
  console.log(
    JSON.stringify({
      timestamp: new Date().toISOString(),
      method: request.method,
      url: redactSensitiveQuery(request.url),
      pathname,
      searchParams: redactSearchParams(request.nextUrl.searchParams),
      ip: request.headers.get("x-forwarded-for") || request.headers.get("x-real-ip") || "",
      userAgent: request.headers.get("user-agent") || "",
      referer: redactSensitiveQuery(request.headers.get("referer") || ""),
      locale: detectedLocale,
    })
  )
}

export function setPathHeaders(response: NextResponse, request: NextRequest): void {
  response.headers.set("x-pathname", request.nextUrl.pathname)
  response.headers.set("x-full-path", request.nextUrl.pathname + request.nextUrl.search)
}

/**
 * www/testwww 生产环境由 nginx 统一处理 /en 去前缀，middleware 跳过避免重复 308。
 * 开发环境及独立域名（news.metal.com 等）仍由 middleware 兜底。
 */
export function shouldSkipDefaultLocaleRedirect(request: NextRequest): boolean {
  if (process.env.NODE_ENV === "development") {
    return false
  }
  return isWwwMetalHost(request)
}

/**
 * 非 www 或开发环境下，将含默认语言前缀的 URL 308 重定向到无前缀规范 URL。
 */
export function applyDefaultLocaleRedirectIfNeeded(
  request: NextRequest,
  options: RedirectDefaultLocalePrefixOptions = {}
): NextResponse | null {
  if (shouldSkipDefaultLocaleRedirect(request)) {
    return null
  }
  return redirectDefaultLocalePrefix(request, options)
}

/**
 * 将 next-intl 产生的 307 临时重定向提升为 308；www 生产环境跳过（nginx 已处理）。
 */
export function promoteIntlRedirectIfNeeded(
  request: NextRequest,
  response: NextResponse,
  defaultLocale = "en"
): NextResponse {
  if (shouldSkipDefaultLocaleRedirect(request)) {
    return response
  }
  return promoteTemporaryRedirectToPermanent(request, response, defaultLocale)
}

export function patchLocaleRedirectForWww(
  request: NextRequest,
  response: NextResponse,
  options: { locales: readonly string[]; subprojectSegment: string }
): void {
  const host = request.headers.get("x-forwarded-host") || request.headers.get("host") || request.nextUrl.host
  if (!host || !WWW_HOST_REGEXP.test(host)) return

  const location = response.headers.get("location")
  if (!location) return

  const target = new URL(location, request.url)
  const segments = target.pathname.split("/").filter(Boolean)
  const locale = segments[0]
  if (!locale || !options.locales.includes(locale)) return
  if (segments[1] === options.subprojectSegment) return

  const remainingPath = segments.slice(1).join("/")
  target.pathname = remainingPath
    ? `/${locale}/${options.subprojectSegment}/${remainingPath}`
    : `/${locale}/${options.subprojectSegment}`

  const isAbsoluteLocation = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(location)
  response.headers.set(
    "location",
    isAbsoluteLocation ? target.toString() : `${target.pathname}${target.search}${target.hash}`
  )
}

/**
 * 清除认证 token cookie
 * @param response - NextResponse 对象
 * @param authTokenKey - 认证 token 的 cookie key
 */
export function clearAuthCookie(response: NextResponse, authTokenKey: string): void {
  response.cookies.delete(authTokenKey)
  response.cookies.set(authTokenKey, "", {
    domain: ".metal.com",
    path: "/",
    maxAge: 0,
  })
}

/**
 * 设置设备相关的 headers 和 cookie
 * @param response - NextResponse 对象
 * @param isMobile - 是否为移动设备
 */
export function setDeviceHeaders(response: NextResponse, isMobile: boolean): void {
  response.headers.set("Accept-CH", "Sec-CH-UA-Mobile, Viewport-Width")
  response.headers.set("Vary", "Sec-CH-UA-Mobile, Viewport-Width, User-Agent")
  response.cookies.set("x-device", isMobile ? "mobile" : "desktop", { path: "/", sameSite: "lax" })
}
